import { defineStore } from 'pinia';
import { api } from 'src/boot/axios';
import { getResponseData } from 'utils';
import { useAuthStore } from './auth-store';

type PermissionCheckInput = {
  can: string;
  unit_id?: number;
  region_id?: number;
};

type PermissionScope = {
  organisation: boolean;
  regions: number[];
  units: number[];
};

type PermissionValue = {
  allow: PermissionScope;
  deny?: PermissionScope;
};

type PermissionEntry = Record<string, PermissionValue>;

let refreshTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleRefresh(ttlMinutes: number) {
  if (refreshTimer !== null) {
    clearTimeout(refreshTimer);
  }
  const ms = ttlMinutes * 60_000;
  const expiry = Date.now() + ms;
  localStorage.setItem('permissionsRefreshAt', expiry.toString());
  refreshTimer = setTimeout(runRefresh, ms);
}

function restoreRefresh(msLeft: number) {
  if (refreshTimer !== null) {
    clearTimeout(refreshTimer);
  }
  refreshTimer = setTimeout(runRefresh, msLeft);
}

async function runRefresh() {
  // Only refresh if user is logged in
  const authStore = useAuthStore();
  if (!authStore.isLoggedIn()) {
    return;
  }

  try {
    const permissionsStore = usePermissionsStore();
    const resp = await api.get('/api/v1/acl');
    const permissionsData = getResponseData(resp)?.user?.permissions as [];
    permissionsStore.setPermissions(permissionsData);
    // Only reschedule on success
    scheduleRefresh(1);
  } catch {
    // Don't reschedule on failure - user may have been logged out
  }
}

export const usePermissionsStore = defineStore('permissions', {
  state: () => ({
    permissions: null as null | Record<string, any>,
  }),
  actions: {
    // Private method to check if current user is a superuser
    _isSuperuser(): boolean {
      const authStore = useAuthStore();

      // Both conditions must be true for superuser status
      const hasAuthSuperuser = authStore.isSuperuser();

      // Check if user has *:ALLOW:* permission with organisation: true
      const hasGlobalOrgAllowAnyPermission = !!(
        this.permissions &&
        Array.isArray(this.permissions) &&
        this.permissions.some((permissionEntry: any) => {
          const globalPermission = permissionEntry['*:ALLOW:*'];
          return globalPermission?.allow?.organisation === true;
        })
      );

      return hasAuthSuperuser && hasGlobalOrgAllowAnyPermission;
    },

    setPermissions(permissions: Record<string, any>) {
      // The ACL endpoint is polled roughly once a minute, and each response is a
      // freshly-parsed array — so assigning it wholesale swaps in brand-new
      // object references even when nothing has actually changed. That silently
      // invalidates every computed and watcher that reads permissions (e.g. the
      // unit dashboard re-fetches its returns on each poll). Compare by
      // serialised value and only update when the ACL has genuinely changed, so
      // the reference stays stable and downstream reactivity does not fire on a
      // no-op refresh.
      const isUnchanged =
        this.permissions !== null &&
        JSON.stringify(this.permissions) === JSON.stringify(permissions);
      if (isUnchanged) return;

      this.permissions = permissions;
      this.cachePermissionsData(permissions);
    },
    cachePermissionsData(data: Record<string, any>) {
      if (data) {
        localStorage.setItem('permissions', JSON.stringify(data));
      }
    },
    fetchPermissionsData() {
      return api.get('/api/v1/acl');
    },
    getPermissions() {
      const cachedPermissionsData = localStorage.getItem('permissions');
      if (cachedPermissionsData) {
        this.permissions = JSON.parse(cachedPermissionsData) as [];
        return;
      }

      // Only fetch from API if user is logged in
      const authStore = useAuthStore();
      if (!authStore.isLoggedIn()) {
        return;
      }

      this.fetchPermissionsData().then((response) => {
        const permissionsData = getResponseData(response)?.user
          ?.permissions as [];
        this.setPermissions(permissionsData);
      });
    },
    clear() {
      this.permissions = null;
    },
    /**
     * Helper to find a permission entry by key from the permissions array.
     * Permissions are stored as an array of objects where each object has
     * the permission key as a property.
     */
    _findPermission(permissionKey: string): PermissionValue | null {
      if (!this.permissions || !Array.isArray(this.permissions)) {
        return null;
      }

      for (const entry of this.permissions as PermissionEntry[]) {
        if (entry[permissionKey]) {
          return entry[permissionKey];
        }
      }
      return null;
    },

    can({ can, unit_id, region_id }: PermissionCheckInput): boolean {
      // Create a collapsed group for all the detailed logs
      console.groupCollapsed(
        "🔒 PERMISSION CHECK: '" +
          can +
          "' with unit=" +
          unit_id +
          ', region=' +
          region_id
      );

      // Check if user is superuser - if so, grant all permissions
      if (this._isSuperuser()) {
        console.log('👑 SUPERUSER: Permission automatically granted');
        console.groupEnd();
        console.log('✅ GRANTED: ' + can + ' - Superuser privileges');
        return true;
      }

      if (!this.permissions?.length) {
        console.log('❌ No permissions loaded!');
        console.groupEnd();
        return false;
      }

      // Decompose into parts to build the permission keys
      const parts = can.split(':').filter((part) => part !== 'ALLOW');
      console.log('🔍 Split parts from ' + can + ':', parts);
      const topic = parts[0];
      const action = parts[1];
      console.log('🔍 Extracted topic=' + topic + ', action=' + action);

      const allowKey = `${topic}:ALLOW:${action}`;
      const denyKey = `${topic}:DENY:${action}`;
      console.log("🔑 Looking for permission key: '" + allowKey + "'");

      // Find the permission entries
      const allowPermission = this._findPermission(allowKey);
      const denyPermission = this._findPermission(denyKey);

      console.log('📋 Found ALLOW permission:', allowPermission);
      console.log('📋 Found DENY permission:', denyPermission);

      // 1. Check DENY first (deny overrides allow at every level)
      if (denyPermission?.deny) {
        const deny = denyPermission.deny;

        // Check organisation-level deny
        if (deny.organisation) {
          console.log('❌ ORGANISATION level DENY found');
          console.groupEnd();
          console.log('❌ DENIED: ' + can + ' - Organisation-level DENY');
          return false;
        }

        // Check region-level deny
        if (region_id != null && deny.regions?.includes(region_id)) {
          console.log('❌ REGION level DENY found for region_id: ' + region_id);
          console.groupEnd();
          console.log('❌ DENIED: ' + can + ' - Region-level DENY');
          return false;
        }

        // Check unit-level deny
        if (unit_id != null && deny.units?.includes(unit_id)) {
          console.log('❌ UNIT level DENY found for unit_id: ' + unit_id);
          console.groupEnd();
          console.log('❌ DENIED: ' + can + ' - Unit-level DENY');
          return false;
        }
      }

      // 2. Check ALLOW (from narrow to broad scope)
      if (allowPermission?.allow) {
        const allow = allowPermission.allow;

        // Check unit-level allow (most specific)
        if (unit_id != null && allow.units?.includes(unit_id)) {
          console.log('✅ UNIT level ALLOW found for unit_id: ' + unit_id);
          console.groupEnd();
          console.log('✅ GRANTED: ' + can + ' - Unit-level permission');
          return true;
        }

        // Check region-level allow
        if (region_id != null && allow.regions?.includes(region_id)) {
          console.log(
            '✅ REGION level ALLOW found for region_id: ' + region_id
          );
          console.groupEnd();
          console.log('✅ GRANTED: ' + can + ' - Region-level permission');
          return true;
        }

        // Check organisation-level allow (broadest)
        if (allow.organisation) {
          console.log('✅ ORGANISATION level ALLOW found');
          console.groupEnd();
          console.log(
            '✅ GRANTED: ' + can + ' - Organisation-level permission'
          );
          return true;
        }

        // If no specific unit_id or region_id provided, check if user has ANY scope
        // This allows navigation to pages when user has scoped permissions
        if (unit_id == null && region_id == null) {
          const hasAnyScope =
            (allow.regions?.length ?? 0) > 0 || (allow.units?.length ?? 0) > 0;

          if (hasAnyScope) {
            console.log('✅ User has scoped permission (general access check)');
            console.groupEnd();
            console.log('✅ GRANTED: ' + can + ' - Scoped permission exists');
            return true;
          }
        }
      }

      // No matching permission found
      console.log('❌ No matching ALLOW permission found for ' + allowKey);
      console.log(
        '  • UNIT level: ' +
          (unit_id != null
            ? 'Checked but not found'
            : 'Not applicable (no unit_id provided)')
      );
      console.log(
        '  • REGION level: ' +
          (region_id != null
            ? 'Checked but not found'
            : 'Not applicable (no region_id provided)')
      );
      console.log('  • ORGANISATION level: Checked but not found');

      console.groupEnd();
      console.log('❌ DENIED: ' + can + ' - No matching ALLOW permission');

      return false;
    },
    /**
     * Returns the allowed region IDs for a given permission key.
     */
    getAllowedRegionIds(permissionKey: string): number[] {
      const permission = this._findPermission(permissionKey);
      return permission?.allow?.regions ?? [];
    },

    getAllowedUnitIds(permissionKey: string): number[] {
      const permission = this._findPermission(permissionKey);
      return permission?.allow?.units ?? [];
    },

    setRestoreRefresh(ttlMinutes: number) {
      restoreRefresh(ttlMinutes);
    },
    setScheduleRefresh(ttlMinutes: number) {
      scheduleRefresh(ttlMinutes);
    },
  },
});
